import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SITE_URL, getSeoForPath } from '../seo';

// Finds an existing meta/link tag or creates it, so index.html stays the
// source of the initial values and this only has to keep them in sync.
const upsertTag = (selector, create) => {
    let el = document.head.querySelector(selector);
    if (!el) {
        el = create();
        document.head.appendChild(el);
    }
    return el;
};

const setMeta = (attr, name, content) => {
    const el = upsertTag(`meta[${attr}="${name}"]`, () => {
        const tag = document.createElement('meta');
        tag.setAttribute(attr, name);
        return tag;
    });
    el.setAttribute('content', content);
};

/**
 * Keeps the document title, description, canonical URL, and social preview
 * tags in step with the current route. Renders nothing.
 *
 * Every route is served from the same index.html, so without this each page
 * would share the home page's title and description.
 */
const Seo = () => {
    const { pathname } = useLocation();

    useEffect(() => {
        const { title, description, noindex } = getSeoForPath(pathname);

        // pathname excludes the router basename, so the canonical URL points at
        // the production location even when built for /demo or /staging.
        const canonicalUrl = `${SITE_URL}${pathname === '/' ? '/' : pathname}`;

        document.title = title;
        setMeta('name', 'description', description);
        setMeta('name', 'robots', noindex ? 'noindex, follow' : 'index, follow');

        setMeta('property', 'og:title', title);
        setMeta('property', 'og:description', description);
        setMeta('property', 'og:url', canonicalUrl);

        const canonical = upsertTag('link[rel="canonical"]', () => {
            const tag = document.createElement('link');
            tag.setAttribute('rel', 'canonical');
            return tag;
        });
        canonical.setAttribute('href', canonicalUrl);
    }, [pathname]);

    return null;
};

export default Seo;
